import { useMemo } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { TripAdvisorReviews } from "@b2bportal/lodging-api/lib/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, TripadvisorRatingIcon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./TripAdvisorRatingOneRow.module.scss";

export type TripAdvisorRatingOneRowProps = PlatformComponentProps & {
  tripAdvisorReviews: TripAdvisorReviews;
};

export const TripAdvisorRatingOneRow = ({
  tripAdvisorReviews,
  className,
}: TripAdvisorRatingOneRowProps) => {
  const styles = useHotelsStyles(
    CoreHotelsComponent.TripAdvisorRating,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, "TripAdvisorRatingOneRow");

  const { t } = useI18nContext();

  const scoreDecimal = tripAdvisorReviews.overallScore * 10;

  const label = useMemo(() => {
    if (scoreDecimal >= 9) {
      return t("hotels.rating.excellent");
    } else if (scoreDecimal >= 8) {
      return t("hotels.rating.veryGood");
    } else if (scoreDecimal >= 6) {
      return t("hotels.rating.good");
    } else if (scoreDecimal >= 4) {
      return t("hotels.rating.average");
    } else {
      return t("hotels.rating.poor");
    }
  }, [scoreDecimal, t]);

  const scoreLabel = useMemo(() => {
    return scoreDecimal.toFixed(1);
  }, [scoreDecimal]);

  const reviewCountText = useMemo(() => {
    return t("hotels.rating.reviewCount", {
      count: tripAdvisorReviews.reviewsCount,
    });
  }, [tripAdvisorReviews.reviewsCount, t]);

  if (!tripAdvisorReviews) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      <div className={cn("score")}>{scoreLabel}</div>
      <div className={cn("label")}>{label}</div>
      <Icon
        useCurrentColor={false}
        className={cn("tripadvisor-icon")}
        iconName={IconNameEnum.tripadvisor}
      />
      <TripadvisorRatingIcon
        scoreOutOfFive={Math.round(tripAdvisorReviews.overallScore * 10) / 2}
        className={cn("rating-dots")}
      />
      <div className={cn("separator")}>•</div>
      <div className={cn("review-count")}>{reviewCountText}</div>
    </div>
  );
};
